<!--
	"Comment" trigger. Self-contained: reads the selected feature + active app
	from stores and derives the report draft itself, so it drops into the shared
	Attributes panel with no prop threading. Renders nothing unless the active app
	opted in (AppConfig.attributes.issueReporting), the reporter is enabled for
	this env (issueReporterUrl, derived from PUBLIC_API_HOST), and the selected
	feature has a usable coordinate.
-->
<script lang="ts">
	import { featureSelectionStore } from '#lib/stores/featureSelectionStore.svelte.js';
	import { appStore } from '#lib/stores/appStore.svelte.js';
	import { issueReporterUrl } from '#lib/config.js';
	import { buildReportDraft, type ReportDraft } from './buildReport';
	import { gatherReportContext } from './reportContext';
	import ReportDataIssueModal from './ReportDataIssueModal.svelte';
	import IconChat from '#lib/assets/chat-bubble-left-right.svg?raw';

	let open = $state(false);
	// Built on click so the captured context (map view-state etc.) is fresh.
	let draft = $state<ReportDraft | null>(null);

	const feature = $derived(featureSelectionStore.current);
	const systemId = $derived(featureSelectionStore.currentSystemId);
	const enabled = $derived(
		Boolean(issueReporterUrl) && appStore.app?.attributes?.issueReporting === true
	);
	// Whether the current feature can be reported (cheap check, no context capture).
	const reportable = $derived(
		enabled &&
			!!feature &&
			!!appStore.app &&
			buildReportDraft(feature, appStore.app.id, systemId) !== null
	);

	function openReport() {
		if (!feature || !appStore.app) return;
		const built = buildReportDraft(feature, appStore.app.id, systemId, gatherReportContext());
		if (!built) return;
		draft = built;
		open = true;
	}
</script>

<!--
	Renders as one segment of the Attributes panel's action bar (ButtonGroup):
	the `segment` class is styled by the bar. The modal is a sibling so its
	lifetime is independent of `reportable`; it's a `<dialog>` (display:none when
	closed, top layer when open), so it never occupies a segment slot.
-->
{#if reportable}
	<button type="button" class="segment" onclick={openReport}>
		<!-- IconChat is a trusted static SVG asset (CLAUDE.md SVG rule) -->
		<!-- eslint-disable-next-line svelte/no-at-html-tags -->
		{@html IconChat}
		<span>Comment</span>
	</button>
{/if}
{#if draft}
	<ReportDataIssueModal {open} {draft} onclose={() => (open = false)} />
{/if}
